import React from 'react';
import {
  Create, NumberInput, SimpleForm, TextInput, maxLength, minLength, required, useGetOne, useNotify, useRedirect,
} from 'react-admin';
import { Alert, Card, CardContent, Stack, Typography } from '@mui/material';
import DateTimeInputUTC from '../../components/DateTimeInputUTC';
import CheckChainUsesModule, { ModuleKey } from '../../components/CheckChainUsesModule';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';
import { EVENTS, track } from '../../lib/analytics';
import { ROLETA_RESOURCE } from '../../models/roleta';
import RoletaImageInput from './RoletaImageInput';

const Section: React.FC<{ title: string; children: React.ReactNode; hint?: string }> = ({ title, children, hint }) => (
  <Card sx={{ mb: 2, width: '100%' }}>
    <CardContent>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: hint ? 0 : 2 }}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {hint}
        </Typography>
      )}
      {children}
    </CardContent>
  </Card>
);

const RoletaCreate: React.FC = (props: any) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const chainId = getChainId();
  const { data: chain } = useGetOne('chains', { id: chainId }, { enabled: !!chainId });

  const transform = (data: any) => ({ ...data, chainId });

  const onSuccess = (data: any) => {
    track(EVENTS.ROLETA_PUBLISHED, { chain_id: chainId });
    notify('Roleta criada em rascunho. Monte as fatias e ative quando estiver pronta.', { type: 'success' });
    redirect('show', ROLETA_RESOURCE, data.id);
  };

  return (
    <CheckChainUsesModule module={ModuleKey.ROLETA}>
      <Permission permission={PERMISSIONS.GIFT}>
        <Create
          {...props}
          resource={ROLETA_RESOURCE}
          transform={transform}
          mutationOptions={{ onSuccess }}
          title="Criar roleta"
        >
          <SimpleForm sx={{ maxWidth: 920 }} defaultValues={{ maxExtraSpinsPerTicket: 3 }}>
            {chain && !chain.useGift && (
              <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>
                Rede sem Clube de Vantagens habilitado não pode ter roleta. Habilite o Clube antes de ativar esta roleta —
                é ele que concede os giros e entrega os prêmios.
              </Alert>
            )}

            <Section
              title="Identificação"
              hint="O nome aparece para o cliente no app. Use algo curto: 'Roleta de Setembro', 'Roleta da Volta às Aulas'."
            >
              <TextInput
                source="name"
                label="Nome da roleta"
                validate={[required(), minLength(3), maxLength(120)]}
                fullWidth
                helperText={false}
              />
              <TextInput
                source="description"
                label="Descrição (opcional)"
                multiline
                rows={3}
                fullWidth
                helperText={false}
              />
              <RoletaImageInput
                source="imageUrl"
                label="Banner (opcional)"
                kind="banner"
                helperText="Faixa larga no topo da tela da roleta. JPG/PNG/WebP até 5MB."
              />
              <RoletaImageInput
                source="centerImageUrl"
                label="Arte do centro da roda (opcional)"
                kind="center"
                helperText="Fica no miolo da roda, que não gira. Vazio = logo da rede. Use para dar cara de campanha (Natal, Copa)."
              />
              <TextInput
                source="regulationUrl"
                label="Link do regulamento (opcional)"
                fullWidth
                helperText="Endereço da página com as regras da campanha, exibido para o cliente."
              />
            </Section>

            <Section title="Período" hint="Fora deste intervalo a roleta não aparece no app e não concede giro.">
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
                <DateTimeInputUTC
                  source="startAt"
                  label="Início"
                  validate={[required()]}
                  sx={{ flex: 1 }}
                  helperText={false}
                />
                <DateTimeInputUTC
                  source="endAt"
                  label="Fim"
                  validate={[required()]}
                  sx={{ flex: 1 }}
                  helperText="Precisa ser depois do início"
                />
              </Stack>
            </Section>

            <Section title="Regras do giro" hint="Dá para mudar depois, mesmo com a roleta ativa.">
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
                <NumberInput
                  source="spinValidityDays"
                  label="Validade do giro (dias)"
                  min={1}
                  sx={{ flex: 1 }}
                  helperText="Deixe vazio para o giro valer até o fim da roleta"
                />
                <NumberInput
                  source="maxExtraSpinsPerTicket"
                  label='Máximo de "gire de novo" seguidos'
                  min={0}
                  sx={{ flex: 1 }}
                  helperText="Evita corrente infinita quando a fatia de novo giro tem peso alto"
                />
              </Stack>
            </Section>
          </SimpleForm>
        </Create>
      </Permission>
    </CheckChainUsesModule>
  );
};

export default RoletaCreate;
